import { Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { Card } from '../components/ui/card';
import { MfaEnrollFlow } from '../components/security/MfaEnrollFlow';

/**
 * Enforces the org MFA policy in the UI (Feature 25, Phase B). A password user
 * whose org requires MFA but who hasn't enrolled is routed into a mandatory
 * full-screen enrollment — mirroring the onboarding gate, so it can't be skipped
 * by navigating straight to an app route. The backend MfaEnrolledGuard enforces
 * the same server-side. Runs inside ProtectedRoute + OnboardingGate, so `user`
 * is present and past onboarding.
 */
export function MfaEnforcementGate() {
  const user = useAuthStore((s) => s.user);
  const reloadUser = useAuthStore((s) => s.reloadUser);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  if (!user || !user.mfaEnforced || user.mfaEnabled) {
    return <Outlet />;
  }

  async function onSignOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-4 py-10">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-iris">
            {user.organizationName}
          </p>
          <h1 className="mt-2 font-display text-2xl font-bold text-bone">
            Two-factor authentication is required
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-muted">
            Your organization requires every member to secure their account with an authenticator
            app before continuing. It only takes a minute.
          </p>
        </div>

        <Card className="p-6">
          {/* Release the gate only after the user has saved their recovery codes
              (reloadUser refreshes mfaEnabled + the token's mfaPending claim). */}
          <MfaEnrollFlow onDone={() => void reloadUser()} doneLabel="Continue to Lizrd" />
        </Card>

        <p className="text-center text-xs text-muted">
          Don&rsquo;t have an authenticator handy?{' '}
          <button type="button" onClick={onSignOut} className="text-iris hover:text-iris-strong">
            Sign out
          </button>{' '}
          and come back when you do.
        </p>
      </div>
    </div>
  );
}
